/**
 * Bulk Upload utilities — CSV parsing and validation.
 *
 * Expected CSV format:
 *   title,description,price,currency,category
 *   "Beach House","Nice listing",100,EUR,accommodation
 *
 * Required columns: title, price
 * Optional columns: description, currency (default: EUR), category
 */

export const REQUIRED_COLUMNS = ['title', 'price'];
export const OPTIONAL_COLUMNS = ['description', 'currency', 'category'];
export const MAX_ROWS = 100;

/**
 * Parse a CSV string into an array of row objects.
 * Handles quoted fields with commas inside.
 * @param {string} csvText
 * @returns {{ headers: string[], rows: Object[] }}
 */
export const parseCsv = (csvText) => {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const fields = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        fields.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    fields.push(current.trim());
    return fields;
  };

  const headers = parseRow(lines[0]).map(h => h.toLowerCase().trim());
  const rows = lines.slice(1, MAX_ROWS + 1).map((line, index) => {
    const values = parseRow(line);
    const row = { _row: index + 2 };
    headers.forEach((h, i) => { row[h] = values[i] || ''; });
    return row;
  });

  return { headers, rows };
};

/**
 * Validate a parsed row against required columns.
 * @param {Object} row
 * @param {string[]} headers
 * @returns {string[]} — array of error messages
 */
export const validateRow = (row, headers) => {
  const errors = [];

  REQUIRED_COLUMNS.forEach(col => {
    if (!headers.includes(col)) {
      errors.push(`Missing required column: ${col}`);
    } else if (!row[col] || String(row[col]).trim() === '') {
      errors.push(`Required field "${col}" is empty`);
    }
  });

  if (row.price !== undefined && isNaN(parseFloat(row.price))) {
    errors.push('Price must be a number');
  }

  if (row.price !== undefined && parseFloat(row.price) < 0) {
    errors.push('Price cannot be negative');
  }

  return errors;
};

/**
 * Check that required headers are present.
 * @param {string[]} headers
 * @returns {string[]} — missing required columns
 */
export const validateHeaders = (headers) =>
  REQUIRED_COLUMNS.filter(col => !headers.includes(col));
